/** 订单 —— 表 + 详情。详情里必须有「凭什么下的这一单」，否则这一屏没价值。
 *
 * 规格 `docs/design/screens/orders.md`。数据来源：
 * `GET /orders`（本地账 `orders`，后端直接给 `avg_px` / `commissions`，裁决 B10）、
 * `GET /fills`（该单成交明细）、`GET /decisions`（凭什么）、`GET /runs/{run_id}`（指纹与版本）、
 * `GET /dispositions`（该单对应的信号下场）。所有账目端点按页头选中的那一本账取（`?run_id=`）。
 */

import { useSearchParams } from "react-router";

import {
  useDecisionsBook,
  useDispositionsBook,
  useFillsBook,
  useOrdersBook,
  useRunTrace,
  type G2Order,
  type G2Trade,
} from "../../api/g2";
import { useStatus } from "../../api/queries";
import { Card, Kpi, Lede, PageHead } from "../../components/Card";
import { Pill, SidePill, statusTone } from "../../components/Pill";
import { QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { DASH, clock, money, num, short, ts } from "../../lib/format";
import { fillGap, isRejected, tradesOf, traceItems, versionItems, type TraceItem } from "../../lib/orders";
import { BookSelector, InlineError, Missing, useBookParams } from "./Book";

export function OrdersScreen(): React.ReactNode {
  const [book] = useBookParams();
  const status = useStatus();
  const orders = useOrdersBook(book);
  const fills = useFillsBook(book);
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("id");

  if (orders.isPending) return <QueryState status="pending" error={null} what="订单">{null}</QueryState>;
  if (orders.isError) {
    return (
      <>
        <PageHead title="订单" actions={<BookSelector envelope={null} />} />
        <QueryState status="error" error={orders.error} what="订单（GET /api/v1/orders）" refetch={() => void orders.refetch()}>
          {null}
        </QueryState>
      </>
    );
  }

  const data = orders.data;
  const list = data.rows;
  const rejected = list.filter(isRejected).length;
  const filled = list.filter((order) => order.status === "FILLED").length;
  const commissions = list.reduce((sum, order) => sum + order.commissions, 0);
  const selected = list.find((order) => order.client_order_id === selectedId) ?? null;
  const neverRan = !book.run_id && status.isSuccess && status.data.current_run === null;

  const select = (id: string | null) => {
    const copy = new URLSearchParams(params);
    if (id) copy.set("id", id);
    else copy.delete("id");
    setParams(copy);
  };

  return (
    <>
      <PageHead
        title="订单"
        sub={
          <>
            {list.length} 笔 · 其中 {rejected} 笔被拒
            {fills.isSuccess ? ` · 成交 ${fills.data.rows.length} 笔` : fills.isError ? " · 成交未取到" : ""}
          </>
        }
        actions={<BookSelector envelope={data} />}
      />
      <Lede>
        订单表就是本地账（唯一真源）的 <code>orders</code>，每行同时给本地单号与券商单号；被拒的单留在表里并写原因——
        「没记账」不等于「没下单」。均价与手续费由后端按该单成交直接给出（不是前端推导）。
      </Lede>

      <div className="grid g4" style={{ marginBottom: 12 }}>
        <Kpi label="订单数" value={num(list.length, 0)} detail="GET /orders · 本地账" />
        <Kpi label="已成交" value={num(filled, 0)} detail="status = FILLED" />
        <Kpi label="被拒" value={<span className={rejected ? "neg" : "dim"}>{num(rejected, 0)}</span>} detail="被拒的单必须带原因" />
        <Kpi label="手续费合计" value={money(commissions)} detail="成交逐笔加总，成本项（不是盈亏）" />
      </div>

      <div className="split wide">
        <Card title="订单流" nopad>
          <Table
            cols={[
              { label: "时间", width: "74px", mono: true },
              { label: "本地单号", mono: true },
              { label: "标的" },
              { label: "方向" },
              { label: "数量", num: true },
              { label: "已成交", num: true },
              { label: "均价", num: true },
              { label: "状态" },
              { label: "券商单号", mono: true },
              { label: "手续费（成本项）", num: true },
            ]}
            rows={list.map((order) => ({
              key: order.client_order_id,
              cls: isRejected(order) ? "warnrow" : "",
              selected: order.client_order_id === selectedId,
              onClick: () => select(order.client_order_id),
              cells: [
                <span className="mono" title={ts(order.ts_event)}>{clock(order.ts_event)}</span>,
                <span className="mono">{short(order.client_order_id, 22)}</span>,
                order.instrument_id || <Missing why="旧账本拒单未记标的" />,
                <SidePill side={order.side} />,
                num(order.quantity, 0),
                num(order.filled_qty, 0),
                order.avg_px === null ? <span className="faint">无成交</span> : num(order.avg_px, 4),
                <span title={order.reason ?? undefined}>
                  <Pill tone={statusTone(order.status)}>{order.status}</Pill>
                  {order.reason ? <span className="neg"> {order.reason}</span> : null}
                </span>,
                order.venue_order_id ? <span className="mono">{order.venue_order_id}</span> : <span className="faint">未回报</span>,
                money(order.commissions),
              ],
            }))}
            empty={
              neverRan
                ? "还没有任何运行，本地账为空；去运行与回放屏跑一次"
                : <EmptyRun runId={data.run_id} />
            }
            tall
          />
        </Card>
        <OrderDetail order={selected} trades={fills.data?.rows ?? null} fillsError={fills.error} refetchFills={() => void fills.refetch()} />
      </div>
    </>
  );
}

function EmptyRun({ runId }: { runId: string | null }): React.ReactNode {
  const run = useRunTrace(runId);
  const accepted = run.data?.accepted;
  return (
    <>
      该运行没有下单（受理 {accepted ? accepted.length : "?"} 条信号，未下单原因见
      <a href="#attribution"> 逐单归因屏</a>）
    </>
  );
}

function OrderDetail({
  order,
  trades,
  fillsError,
  refetchFills,
}: {
  order: G2Order | null;
  trades: G2Trade[] | null;
  fillsError: unknown;
  refetchFills: () => void;
}): React.ReactNode {
  if (!order) {
    return (
      <Card title="订单详情">
        <div className="empty">选一笔订单看详情</div>
      </Card>
    );
  }
  const mine = trades ? tradesOf(order.client_order_id, trades) : [];
  const gap = trades ? fillGap(order, mine) : null;
  return (
    <div>
      <Card title="订单详情" sub={order.client_order_id} cls={isRejected(order) ? "fatal" : ""}>
        <dl className="kv">
          <dt>本地单号</dt>
          <dd>{order.client_order_id}</dd>
          <dt>券商单号</dt>
          <dd>{order.venue_order_id ?? <span className="faint">未回报</span>}</dd>
          <dt>标的 / 方向</dt>
          <dd>
            {order.instrument_id || <Missing why="旧账本拒单未记标的" />} · {order.side ?? DASH}
          </dd>
          <dt>数量 / 已成交</dt>
          <dd>
            {num(order.quantity, 0)} / {num(order.filled_qty, 0)}
          </dd>
          <dt>成交均价</dt>
          <dd>{order.avg_px === null ? "无成交" : num(order.avg_px, 5)}</dd>
          <dt>手续费</dt>
          <dd>{money(order.commissions)}（成本项）</dd>
          <dt>状态</dt>
          <dd>
            <Pill tone={statusTone(order.status)}>{order.status}</Pill>
          </dd>
          <dt>时间</dt>
          <dd>{ts(order.ts_event)}</dd>
          <dt>被拒原因</dt>
          <dd className={order.reason ? "neg" : undefined}>{order.reason ?? "—"}</dd>
        </dl>
      </Card>

      <div style={{ marginTop: 12 }}>
        <Card title="成交明细" sub="GET /fills · 该单">
          {fillsError ? (
            <InlineError endpoint="GET /api/v1/fills" error={fillsError} onRetry={refetchFills} />
          ) : trades === null ? (
            <div className="faint">取数中…</div>
          ) : gap ? (
            <div className={isRejected(order) ? "empty" : "note warn"}>{gap}</div>
          ) : (
            <dl className="kv">
              {mine.map((trade) => (
                <div key={trade.trade_id} style={{ display: "contents" }}>
                  <dt className="mono">{trade.trade_id}</dt>
                  <dd>
                    {num(trade.last_qty, 0)} @ {num(trade.slipped_px, 4)}（撮合 {num(trade.last_px, 4)}）· 手续费 {money(trade.commission)} ·{" "}
                    {ts(trade.ts_event)} · 仓位 {trade.position_id ?? "—"}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 12 }}>
        <WhyCard order={order} />
      </div>
    </div>
  );
}

/** 「凭什么下的这一单」：五项可追 + 版本位，字段直接取 `decisions[client_order_id]`。 */
function WhyCard({ order }: { order: G2Order }): React.ReactNode {
  const [book] = useBookParams();
  const decisions = useDecisionsBook(book);
  const dispositions = useDispositionsBook(book);
  const runId = decisions.data?.run_id ?? book.run_id ?? null;
  const run = useRunTrace(runId);

  if (decisions.isPending) return <Card title="凭什么下的这一单"><div className="faint">取数中…</div></Card>;
  if (decisions.isError) {
    return (
      <Card title="凭什么下的这一单">
        <InlineError endpoint="GET /api/v1/decisions" error={decisions.error} onRetry={() => void decisions.refetch()} />
      </Card>
    );
  }
  const decision = decisions.data.rows[order.client_order_id];
  const disposition = dispositions.data?.rows.find((row) => row.order_id === order.client_order_id) ?? null;
  return (
    <Card title="凭什么下的这一单" sub="GET /decisions · GET /runs/{run_id}">
      {!decision ? <div className="note bad">该订单无决策留痕（缺）——不拿相邻订单或信号顶上</div> : null}
      <TraceList items={traceItems(decision)} />
      <div className="hint" style={{ margin: "8px 0 4px" }}>版本追溯</div>
      {run.isError ? (
        <InlineError endpoint={`GET /api/v1/runs/${runId}`} error={run.error} onRetry={() => void run.refetch()} />
      ) : (
        <TraceList items={versionItems(decision, run.data ?? null)} />
      )}
      <div className="hint" style={{ marginTop: 6 }}>
        数据快照（运行归档）：{run.data?.snapshot_id ?? "—"} · 账目指纹：{short(run.data?.ledger_digest ?? null, 12)}
        {disposition ? (
          <>
            {" "}
            · 信号下场：{disposition.disposition}（<a href="#attribution">去逐单归因</a>）
          </>
        ) : null}
        {dispositions.isError ? <span className="neg"> · 信号下场未取到</span> : null}
      </div>
    </Card>
  );
}

function TraceList({ items }: { items: TraceItem[] }): React.ReactNode {
  return (
    <dl className="kv">
      {items.map((item) => (
        <div key={item.label} style={{ display: "contents" }}>
          <dt>{item.label}</dt>
          <dd>{item.value ?? <Missing why={item.why} />}</dd>
        </div>
      ))}
    </dl>
  );
}
